import * as yup from 'yup'
import { validations } from '@utils/order'
import { ALL_CODES } from '@model/order/ShippingQuote'
import { DELIVER_TO_COUNTRIES } from '@model/locales'

const partialAddressInfoSchema = yup.object({
  city: yup.string().nullable(),
  country: yup.string().nullable().min(3),
  emailAddress: yup.string().defined().email(),
  phone: yup.string().defined().matches(validations.telephone, { message: 'Telephone is invalid' }),
  postCode: yup.string().nullable().matches(validations.zip, { message: 'Zip code is invalid' }),
  streetAddress: yup.string().nullable().min(3),
})

export const returnFormPersonalInfoSchema = yup.object({
  streetAddress: yup.string().nullable().min(3),
  emailAddress: yup.string().min(3).email(),
  phone: yup.string().defined().matches(validations.telephone, { message: 'Telephone is invalid' }),
  firstName: yup.string().defined().min(2),
  lastName: yup.string().defined().min(2),
  country: yup.string().defined().required().oneOf(DELIVER_TO_COUNTRIES),
})

export const accountNumberSchema = yup.string().required().min(8)

const addressInfoSchema = yup.object({
  city: yup.string().defined(),
  country: yup.string().defined().min(2).oneOf(['EE', 'FI', 'LV', 'LT', 'NO', 'SE']),
  emailAddress: yup.string().defined().email(),
  phone: yup.string().defined().matches(validations.telephone, { message: 'Telephone is invalid' }),
  postCode: yup.string().defined().matches(validations.zip, { message: 'Zip code is invalid' }),
  streetAddress: yup.string().defined().min(3),
})

const addressInfoHomeSchema = yup.object({
  city: yup.string().defined(),
  country: yup.string().defined().min(2).oneOf(['EE', 'FI', 'LV', 'LT', 'NO', 'SE']),
  emailAddress: yup.string().defined().email(),
  phone: yup.string().defined().matches(validations.telephone, { message: 'Telephone is invalid' }),
  postCode: yup.string().defined(),
  streetAddress: yup.string().defined().min(3),
})

const returnPaymentInfoSchema = yup.object({
  accountNumber: yup
    .string()
    .when('paymentMethod', ([paymentMethod], schema) =>
      !['adyen', 'esto', 'klarna', 'coPoints'].includes(paymentMethod) ? schema.required().min(8) : schema.nullable()
    ),
  ownerName: yup.string().required(),
  paymentMethod: yup.string().required().min(3),
  bank: yup
    .string()
    .when('paymentMethod', ([paymentMethod], schema) =>
      !['adyen', 'esto', 'klarna', 'coPoints'].includes(paymentMethod) ? schema.required() : schema.nullable()
    ),
})

const anotherReturnItemSchema = yup.object({
  reasonForReturn: yup
    .string()
    .oneOf([
      'DEFECTIVE_PRODUCT',
      'CHANGED_MIND',
      'WRONG_PRODUCT',
      'WRONG_QUANTITY',
      'WONT_REVEAL',
      'NO_DISCOUNT',
      'NO_PRODUCT',
      'NO_SHIPMENT',
      'WRONG_SIZE',
    ]),
  actionValue: yup.string().required().oneOf(['REPLACE', 'GUARANTEE', 'REFUND', 'DISCOUNT']),
  quantity: yup.number().required().min(1),
  itemCode: yup.string().required().min(5),
  itemName: yup.string().required().min(5),
  orderReceived: yup.string().required().length(10),
  orderPlaced: yup.string().required().length(10),
  orderNumber: yup.string().required().min(5),
})

export const anotherReturnItemsSchema = yup.array().of(anotherReturnItemSchema)

export const returnItemSchema = yup.object({
  replacementItemQuantity: yup.number().nullable().min(1),
  clientComment: yup.string().nullable(),
  returnReason: yup
    .string()
    .oneOf([
      'DEFECTIVE_PRODUCT',
      'CHANGED_MIND',
      'WRONG_PRODUCT',
      'WRONG_QUANTITY',
      'WONT_REVEAL',
      'NO_DISCOUNT',
      'NO_PRODUCT',
      'NO_SHIPMENT',
      'WRONG_SIZE',
    ]),
  returnRequestedAction: yup.string().required().oneOf(['REPLACE', 'GUARANTEE', 'REFUND', 'DISCOUNT']),
  itemPimItemNumber: yup.string().required().min(3),
  replacementItemPimItemNumber: yup.string().nullable(),
  itemQuantity: yup.number().required(),
  returnItemStatus: yup.string().required().oneOf(['NEW']),
  returnItemType: yup.string().required().oneOf(['PRODUCT']),
})

export type ReturnItemIface = yup.InferType<typeof returnItemSchema>

const shipmentReturnSchema = yup.object({
  returnStatus: yup.string().required().oneOf(['NEW']),
  packageDeliveryDate: yup.date().required(),
  shipmentNumber: yup.string().required(),
  returnItems: yup.array().of(returnItemSchema),
})

export const shipmentInfoSchema = yup.object({
  firstName: yup.string().defined().min(2),
  lastName: yup.string().defined().min(2),
  itellaRoutingCode: yup.string().nullable(),
  carrierCode: yup.string().defined().required().oneOf(ALL_CODES),
  parcelMid: yup
    .object()
    .when('$deliveryType', ([deliveryType], schema) =>
      deliveryType === 'isHomeDelivery' ? yup.string().nullable() : yup.string().defined().min(1)
    ),
  pickupPointName: yup
    .string()
    .when('$deliveryType', ([deliveryType], schema) =>
      deliveryType === 'isParcelDelivery' ? schema.required().min(2) : schema.nullable()
    ),
  parcelMachineAddressInfo: yup
    .object()
    .when('$deliveryType', ([deliveryType], schema) =>
      deliveryType === 'isParcelDelivery' ? addressInfoSchema.required() : schema.nullable()
    ),
  receivingAddressInfo: yup
    .object()
    .when('$deliveryType', ([deliveryType], schema) =>
      deliveryType === 'isHomeDelivery' ? addressInfoHomeSchema.required() : schema.nullable()
    ),
})

export type ShipmentInfoIface = yup.InferType<typeof shipmentInfoSchema>

export interface ValidateOptions {
  context: {
    type?: string
    deliveryType?: string
  }
}

export const ecwOrderSchema = yup.object({
  orderNumber: yup.string().defined().required().min(5),
  returnType: yup.string().defined().required().oneOf(['CLIENT_INITIATED']),
  customerInfo: yup.object({
    addressInfo: partialAddressInfoSchema,
    firstName: yup.string().defined().required(),
    lastName: yup.string().defined().required(),
  }),
  shipmentReturns: yup.array().of(shipmentReturnSchema).required().min(1),
  shipmentInfo: yup
    .object()
    .when('$type', ([type], schema) => (type === 'refund' ? schema.nullable() : shipmentInfoSchema.required())),
  returnPaymentInfos: yup
    .object()
    .when('$type', ([type], schema) =>
      type === 'refund' || type === 'both-options'
        ? yup.array().of(returnPaymentInfoSchema).required().min(1)
        : schema.nullable()
    ),
})
